import { EEvolveLandingPageTabs, thunkActionLoading } from '@configs'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppDispatch, useAppSelector } from '@redux'
import { message } from 'antd'
import { t } from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { IMediaItem } from 'src/interfaces/gallery'
import {
  getEvolveOurCommunityAction,
  updateEvolveOurCommunityAction,
} from 'src/redux/actions/evolve-landing-management'
import {
  IJoinOurCommunitySchema,
  JoinOurCommunitySchema,
} from './JoinOurCommunitySchema'

interface IDownloadProps {
  activeTab?: string
}

export const useJoinOurCommunity = ({ activeTab }: IDownloadProps) => {
  const dispatch = useAppDispatch()
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [isChange, setIsChange] = useState(false)

  const [showModalSelectMedia, setShowModalSelectMedia] = useState(false)
  const [selectedField, setSelectedField] = useState<
    'desktop' | 'mobile' | null
  >(null)
  const onChangeDesktopRef = useRef<any>(null)
  const onChangeMobileRef = useRef<any>(null)

  // Redux selectors

  const ourCommunity = useAppSelector((state) => state.evolveLanding.community)
  const getOurCommunityActionLoading = useAppSelector(
    (state) =>
      state.evolveLanding.loadings[
        thunkActionLoading.GET_EVOLVE_OUR_COMMUNITY_LOADING
      ]
  )
  const createOurCommunityLoading = useAppSelector(
    (state) =>
      state.evolveLanding.loadings[
        thunkActionLoading.UPDATE_EVOLVE_OUR_COMMUNITY_LOADING
      ]
  )

  const [imageSrc, setImageSrc] = useState('')
  const [imageMobileSrc, setImageMobileSrc] = useState('')

  const isDisabledSaveButton = useMemo(() => {
    return createOurCommunityLoading || false
  }, [createOurCommunityLoading])

  const { handleSubmit, control, setValue, getValues } =
    useForm<IJoinOurCommunitySchema>({
      defaultValues: {
        title: '',
        titleInDutch: '',
        content: '',
        contentInDutch: '',
        mediaId: undefined,
        mediaMobileId: undefined,
        type: 'COMMUNITY',
      },
      resolver: zodResolver(JoinOurCommunitySchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
    })

  const onSubmit = async (data: IJoinOurCommunitySchema) => {
    try {
      const response = await dispatch(
        updateEvolveOurCommunityAction(data)
      ).unwrap()

      if (response.success) {
        message.success({
          content: response?.message ?? t('common:save_success'),
        })
      }
    } catch (error: any) {
      message.error({
        content: error?.message ?? 'Somethings went wrong!',
      })
    }
  }

  const onSave = () => {
    handleSubmit(onSubmit)()
    setIsChange(false)
  }

  const onCloseModalSelectMedia = () => {
    setShowModalSelectMedia(false)
  }

  const onSelectMedia = (data: IMediaItem) => {
    if (selectedField === 'desktop') {
      setImageSrc(data?.baseUrl ?? '')
      setValue('mediaId', data?.id ?? 0)
      onChangeDesktopRef.current(data?.id ?? 0)
    } else if (selectedField === 'mobile') {
      setImageMobileSrc(data?.baseUrl ?? '')
      setValue('mediaMobileId', data?.id ?? 0)
      onChangeMobileRef.current(data?.id ?? 0)
    }

    setShowModalSelectMedia(false)
    setIsChange(true)
  }

  useEffect(() => {
    if (activeTab === EEvolveLandingPageTabs.JOIN_OUR) {
      const getDownload = async () => {
        try {
          const responseGetDownload = await dispatch(
            getEvolveOurCommunityAction()
          ).unwrap()

          if (responseGetDownload.success) {
            setIsChange(false)
          }
        } catch (error: any) {
          message.error({
            content: error.message ?? 'Somethings went wrong!',
          })
        }
      }
      getDownload()
    }
  }, [activeTab, dispatch, setValue])

  useEffect(() => {
    if (ourCommunity) {
      if (Object.keys(ourCommunity).length) {
        setValue('title', ourCommunity?.title || '')
        setValue('titleInDutch', ourCommunity?.titleInDutch || '')
        setValue('content', ourCommunity?.content || '')
        setValue('contentInDutch', ourCommunity?.contentInDutch || '')
        setValue('mediaId', ourCommunity?.mediaId || 0)
        setValue('mediaMobileId', ourCommunity?.mediaMobileId || 0)
        setValue('type', 'COMMUNITY')
        setImageSrc(ourCommunity?.media?.url ?? '')
        setImageMobileSrc(ourCommunity?.mediaMobile?.url ?? '')
      }
    }
  }, [ourCommunity])

  return {
    getOurCommunityActionLoading,
    control,
    setIsChange,
    onChangeDesktopRef,
    setValue,
    setImageSrc,
    setSelectedField,
    setShowModalSelectMedia,
    imageSrc,
    getValues,
    isModalLoading,
    setIsModalLoading,
    onChangeMobileRef,
    isDisabledSaveButton,
    imageMobileSrc,
    onSave,
    isChange,
    showModalSelectMedia,
    onCloseModalSelectMedia,
    onSelectMedia,
  }
}
